import { format } from "date-fns";
import type { Locale } from "date-fns";
import { Namespace, useTypedT } from "@racoon/i18n";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import type { AnomalyDayCount, AnomalyProviderCount } from "../lib/use-anomaly-analytics.hook";

export const AnomalyAnalyticsPanel = ({
  days,
  maxDayCount,
  topProviders,
  otherProvidersCount,
  monthPeriodLabel,
  dateFnsLocale,
}: {
  days: AnomalyDayCount[];
  maxDayCount: number;
  topProviders: AnomalyProviderCount[];
  otherProvidersCount: number;
  monthPeriodLabel: string;
  dateFnsLocale: Locale;
}) => {
  const t = useTypedT(Namespace.Govdash);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-2">
      <CardContentWrapper hasDivider={false} className="flex shrink-0 flex-col overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-200 p-4">
          <p className="min-w-0 flex-1 truncate text-base font-semibold text-text-primary">
            {t("monitor.anomaliesByDay")}
          </p>
          <p className="shrink-0 text-sm font-medium text-text-secondary">
            {t("monitor.anomaliesByDayPeriod", { count: days.length })}
          </p>
        </div>
        <div className="flex h-[180px] items-end gap-2 p-4">
          {days.map((day) => (
            <div key={day.date.toISOString()} className="flex h-full min-w-0 flex-1 flex-col items-center gap-2">
              <p className="text-xs font-bold uppercase text-text-primary">{day.count}</p>
              <div className="flex w-full flex-1 items-end">
                <div
                  className="w-full rounded-sm bg-brand-blue"
                  style={{ height: `${Math.max((day.count / maxDayCount) * 100, 4)}%` }}
                />
              </div>
              <div className="flex flex-col items-center text-text-secondary">
                <span className="text-xs font-medium tracking-tight">{format(day.date, "d")}</span>
                <span className="text-[10px] font-semibold">
                  {format(day.date, "EEEEEE", { locale: dateFnsLocale })}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContentWrapper>

      <CardContentWrapper hasDivider={false} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 p-4">
          <p className="min-w-0 flex-1 truncate text-base font-semibold text-text-primary">
            {t("monitor.topAnomaliesByMvo")}
          </p>
          <p className="shrink-0 text-sm font-medium text-text-secondary">{monthPeriodLabel}</p>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4">
          {topProviders.map((provider) => (
            <div key={provider.organizationId} className="flex items-center justify-between gap-2">
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
                {provider.organizationName}
              </p>
              <p className="shrink-0 text-sm font-medium text-text-primary">{provider.count}</p>
            </div>
          ))}
          {otherProvidersCount > 0 && (
            <div className="flex items-center justify-between gap-2">
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-text-secondary">
                {t("monitor.otherMvos")}
              </p>
              <p className="shrink-0 text-sm font-medium text-text-secondary">{otherProvidersCount}</p>
            </div>
          )}
        </div>
      </CardContentWrapper>
    </div>
  );
};
